import { addMonths, format, startOfMonth } from 'date-fns';
import { useState } from 'react';
import { LuCalendarRange, LuChevronDown, LuChevronLeft, LuChevronRight } from 'react-icons/lu';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/utils/cn';

import { ChartWindow, chartWindowPresets, formatWindow, MAX_WINDOW_MONTHS, monthIndex } from './policy-cashflow-chart';

interface PolicyChartWindowPickerProps {
    value: ChartWindow;
    onChange: (range: ChartWindow) => void;
    today: Date;
}

const fromIndex = (index: number): ChartWindow['start'] => new Date(Math.floor(index / 12), index % 12, 1);

/** Chooses the months the cash-flow chart covers: a preset, or a first and last month from the grid. */
export const PolicyChartWindowPicker = ({ value, onChange, today }: PolicyChartWindowPickerProps) => {
    const [open, setOpen] = useState(false);
    const [year, setYear] = useState(value.start.getFullYear());
    // The first month of a custom range, while the last is still to be picked.
    const [pending, setPending] = useState<number>();
    const now = monthIndex(today);
    const first = monthIndex(value.start);
    const last = first + value.months - 1;

    const openChange = (next: boolean) => {
        setOpen(next);
        if (next) {
            setYear(value.start.getFullYear());
            setPending(undefined);
        }
    };
    const choose = (range: ChartWindow) => {
        onChange(range);
        setOpen(false);
    };
    const pickMonth = (index: number) => {
        if (pending === undefined) return setPending(index);
        const low = Math.min(pending, index);
        const high = Math.min(Math.max(pending, index), low + MAX_WINDOW_MONTHS - 1);
        choose({ start: fromIndex(low), months: high - low + 1 });
    };

    return (
        <Popover open={open} onOpenChange={openChange}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className={cn(
                        'flex h-8 items-center gap-2 rounded-lg border bg-white pr-2 pl-3 text-[13px] font-medium whitespace-nowrap text-[#1F1A19] hover:bg-[#F7F5F3]',
                        open ? 'border-[#2C2424]' : 'border-[#E0DAD5]',
                    )}
                >
                    <LuCalendarRange aria-hidden="true" className="size-4 text-[#6B625E]" />
                    {formatWindow(value)}
                    <LuChevronDown aria-hidden="true" className="size-4 text-[#897E7B]" />
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="end"
                aria-label="Choose a date range"
                className="z-[70] flex w-[444px] overflow-hidden rounded-[10px] border-[#CECAC6] p-0 tracking-[-0.02em]"
            >
                <div className="flex w-36 shrink-0 flex-col gap-px border-r border-[#ECE8E4] bg-[#FCFBFA] p-1.5">
                    <span className="px-2 pt-2 pb-1 text-[10.5px] font-semibold tracking-[0.05em] text-[#897E7B] uppercase">
                        Suggested
                    </span>
                    {chartWindowPresets(today).map((preset) => (
                        <button
                            key={preset.key}
                            type="button"
                            title={formatWindow(preset.window)}
                            className={cn(
                                'flex h-[30px] items-center rounded-md px-2 text-left text-[12.5px] font-medium whitespace-nowrap hover:bg-[#F1EFEC]',
                                formatWindow(preset.window) === formatWindow(value) && 'bg-[#F1EFEC]',
                            )}
                            onClick={() => choose(preset.window)}
                        >
                            {preset.name}
                        </button>
                    ))}
                </div>
                <div className="flex flex-1 flex-col gap-2.5 px-3 pt-2.5 pb-3">
                    <div className="flex items-center gap-1">
                        <button
                            type="button"
                            aria-label="Previous year"
                            className="flex size-7 items-center justify-center rounded-md text-[#5A4F4E] hover:bg-[#F1EFEC] disabled:opacity-40"
                            onClick={() => setYear(year - 1)}
                        >
                            <LuChevronLeft aria-hidden="true" className="size-[18px]" />
                        </button>
                        <span className="flex-1 text-center text-[13px] font-semibold">{year}</span>
                        <button
                            type="button"
                            aria-label="Next year"
                            className="flex size-7 items-center justify-center rounded-md text-[#5A4F4E] hover:bg-[#F1EFEC]"
                            onClick={() => setYear(year + 1)}
                        >
                            <LuChevronRight aria-hidden="true" className="size-[18px]" />
                        </button>
                    </div>
                    <div className="grid flex-1 grid-cols-4 grid-rows-3 gap-1">
                        {Array.from({ length: 12 }, (_, month) => {
                            const index = year * 12 + month;
                            const edge = pending === undefined ? index === first || index === last : index === pending;
                            const inRange = pending === undefined && index >= first && index <= last;
                            return (
                                <button
                                    key={month}
                                    type="button"
                                    aria-label={format(startOfMonth(new Date(year, month, 1)), 'MMMM yyyy')}
                                    aria-pressed={edge}
                                    className={cn(
                                        'min-h-9 rounded-md border border-transparent text-[12.5px]',
                                        edge
                                            ? 'border-[#2C2424] bg-[#2C2424] text-white'
                                            : inRange
                                              ? 'bg-[#F1EFEC]'
                                              : 'bg-white hover:border-[#897E7B]',
                                        index === now && !edge && 'border-[#897E7B] font-semibold',
                                    )}
                                    onClick={() => pickMonth(index)}
                                >
                                    {format(addMonths(new Date(year, 0, 1), month), 'MMM')}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
};
